import React, { memo, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { get } from 'lodash';
import { useTranslation } from '../../../../src/i18n';
import { PRODUCTION_DOCUMENT_TYPES, TP } from '../../../../constants/consts';
import {
  deleteProductionDocumentAction,
  getProductionSinopsisAction,
  updateProductionSynopsisDocumentsAction,
  uploadProductionDocumentsAction,
} from '../../actions';
import { selectLoadingFieldName, selectProductionBooklets, selectProductionSynopsis } from '../../selectors';
import { deleteEntity, getLanguagesAction, getWholeList, useBufferValueAction } from '../../../App/actions';
import endpointConstants, { LANGUAGES_LIST } from '../../../../constants/endpointConstants';
import EditMediaLayout from '../../../../components/EditComponents/EditMediaLayout';
import AddFileModal from '../../../../components/EditComponents/AddFileModal';
import EditFileCard from '../../../../components/EditComponents/EditFileCard';
import ActionStatusModal from '../../../../components/ActionStatusModal';
import TextButton from '../../../../components/TextButton';
import Button from '../../../../components/Button';
import { selectBufferValueData, selectLanguagesOptions } from '../../../App/selectors';
import EditSynopsisCard from './EditSynopsisCard';
import AddSynopsisModal, { synopsisFields, synopsisEmptyValue } from './AddSynopsisModal';
import { EditLayoutHeader } from '../../../../components/EditComponents/EditLayout/EditLayoutHeader';
import NoListItems from '../../../../components/NoListItems';

const useStyles = makeStyles(theme => ({
  title: {
    marginTop: theme.spacing(4),
  },
  inputClassName: {
    flexDirection: 'column',
  },
  actTitle: {
    marginTop: theme.spacing(4),
    display: 'block',
  },
  bookletSection: {
    marginTop: theme.spacing(8),
  },
  noBookletButton: {
    display: 'block',
    margin: 'auto',
  },
  submitButton: {
    display: 'block',
    marginLeft: 'auto',
    marginTop: theme.spacing(4),
  },
}));

const SynopsisSection = ({ production, langs, synopsisData, bufferData, loadingFieldName, booklets }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();

  const [isAddBookletsModalOpened, setAddBookletsModalState] = React.useState(false);
  const [bookletToDelete, setBookletToDelete] = React.useState(null);
  const [synopsisToDelete, setSynopsisToDelete] = React.useState(null);
  const [synopsisToEdit, setSynopsisToEdit] = React.useState(null);

  const getDocuments = () => {
    dispatch(
      getWholeList({
        endpoint: endpointConstants.PRODUCTION_DOCUMENTS,
        sagaRoutine: getProductionSinopsisAction,
        params: {
          queryParams: {
            production_id: production?.id,
            document_type: [
              PRODUCTION_DOCUMENT_TYPES.SYNOPSIS,
              PRODUCTION_DOCUMENT_TYPES.BOOKLET,
              PRODUCTION_DOCUMENT_TYPES.ACT,
            ],
          },
        },
      }),
    );
  };

  useEffect(() => {
    if (!synopsisData?.loadedOnce) {
      getDocuments();
    }
    if (!langs?.length) {
      dispatch(getWholeList({ endpoint: LANGUAGES_LIST, sagaRoutine: getLanguagesAction }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddBookletsModal = () => {
    setAddBookletsModalState(true);
  };

  const uploadFiles = files => {
    const dataToUpload = files.map((file, i) => ({
      production: { id: production?.id },
      ...(file?.file?.type === 'url' ? { url: file?.path } : { file: { data: `@${i}_${file?.file?.name}` } }),
      document_type: PRODUCTION_DOCUMENT_TYPES.BOOKLET,
      content: file?.file?.name,
    }));

    dispatch(
      uploadProductionDocumentsAction({
        fieldName: 'documents',
        data: dataToUpload,
        files: files
          .map((file, fileIndex) => file?.file?.type !== 'url' && [fileIndex, file.file])
          .filter(Boolean)
          .reduce((prev, [fileIndex, fileData]) => {
            // On uploading multiple files we can have same names
            prev[`@${fileIndex}_${fileData?.name}`] = fileData;
            return prev;
          }, {}),
        callback: () => {
          setAddBookletsModalState(false);
        },
      }),
    );
  };

  const deleteFile = file => {
    dispatch(
      deleteEntity({
        endpoint: endpointConstants.PRODUCTION_DOCUMENTS,
        id: file?.id,
        sagaRoutine: useBufferValueAction,
        params: {},
        callback: deletedBookletId => {
          dispatch(deleteProductionDocumentAction(deletedBookletId));
          setBookletToDelete(null);
        },
      }),
    );
  };

  const uploadSynopsis = values => {
    const createdDocuments = [];
    const deletedDocuments = [];
    const updatedDocuments = [];
    const language = get(values, synopsisFields.lang.name);

    const updatedOrCreatedSeparation = (document, type) => {
      const documentWithLang = {
        ...document,
        language: { id: language?.value },
      };
      if (document?.id) {
        updatedDocuments.push(documentWithLang);
      } else createdDocuments.push({ ...documentWithLang, production: { id: production?.id }, document_type: type });
    };

    const synopsis = get(values, synopsisFields.synopsis.name);
    const acts = get(values, 'acts', []);

    updatedOrCreatedSeparation(synopsis, PRODUCTION_DOCUMENT_TYPES.SYNOPSIS);
    acts.forEach(act => updatedOrCreatedSeparation(act, PRODUCTION_DOCUMENT_TYPES.ACT));

    if (acts?.length < synopsisData.acts?.length) {
      synopsisData.acts.forEach(act => {
        if (!acts.find(formAct => formAct?.id === act?.id)) {
          deletedDocuments.push(act?.id);
        }
      });
    }

    dispatch(
      updateProductionSynopsisDocumentsAction({
        createdDocuments,
        updatedDocuments,
        deletedDocuments,
        fieldName: 'synopsis',
        callback: () => {
          getDocuments();
          setSynopsisToEdit(null);
        },
      }),
    );
  };

  const deleteSynopsis = () => {
    dispatch(
      updateProductionSynopsisDocumentsAction({
        createdDocuments: [],
        updatedDocuments: [],
        deletedDocuments: [synopsisToDelete?.synopsis?.id, ...synopsisToDelete?.acts?.map(a => a?.id)],
        fieldName: 'synopsis',
        callback: () => {
          getDocuments();
          setSynopsisToDelete(null);
        },
      }),
    );
  };

  return (
    <>
      <EditLayoutHeader
        title={`${TP}.FN_PRODUCTION_SYNOPSIS`}
        subtitle={`${TP}.FN_ADD_PRODUCTION_SYNOPSIS_INFO`}
        actions={
          <Button variant="outlined" color="secondary" onClick={() => setSynopsisToEdit(synopsisEmptyValue())}>
            {t(`${TP}.FN_ADD_SYNOPSIS`)}
          </Button>
        }
      />
      {synopsisData?.documents?.length > 0 ? (
        synopsisData?.documents.map(doc => (
          <EditSynopsisCard
            key={doc.synopsis?.id}
            document={doc}
            onDelete={() => setSynopsisToDelete(doc)}
            onEdit={() => {
              setSynopsisToEdit(doc);
            }}
          />
        ))
      ) : (
        <NoListItems text={t(`${TP}.FN_NO_SYNOPSIS_ADDED`)} />
      )}
      <EditMediaLayout
        title={t(`${TP}.FN_PRODUCTION_BOOKLET`)}
        subtitle={t(`${TP}.FN_ADD_PRODUCTION_BOOKLET_INFO`)}
        isEmpty={!booklets?.length}
        buttonTitle={`${TP}.FN_UPLOAD`}
        addButtonClick={openAddBookletsModal}
        emptyMessage={`${TP}.FN_NO_BOOKLET_ADDED`}
        className={classes.bookletSection}
      >
        {booklets?.map(booklet => (
          <EditFileCard
            key={booklet?.file?.original}
            file={{
              name: booklet?.content,
              size: booklet?.file?.size,
              type: booklet?.file?.mimetype,
              url: booklet?.url || booklet?.file?.original,
            }}
            onDelete={() => setBookletToDelete(booklet)}
          />
        ))}
      </EditMediaLayout>
      {isAddBookletsModalOpened && (
        <AddFileModal
          isOpened
          onClose={() => setAddBookletsModalState(false)}
          addFiles={uploadFiles}
          title1={`${TP}.FN_ADD_PRODUCTION_BOOKLET`}
          isLoading={loadingFieldName === 'documents'}
        />
      )}
      {bookletToDelete && (
        <ActionStatusModal
          isOpened
          onClose={() => setBookletToDelete(null)}
          type="warning"
          title={t(`${TP}.FN_DELETE_BOOKLET_CONFIRM_TITLE`)}
          buttons={[
            <TextButton onClick={() => setBookletToDelete(null)} key="cancel-button">
              {t(`${TP}.m_CANCEL`)}
            </TextButton>,
            <Button isLoading={bufferData?.loading} onClick={() => deleteFile(bookletToDelete)} key="delete-button">
              {t(`${TP}.m_DELETE`)}
            </Button>,
          ]}
        >
          {t(`${TP}.FN_DELETE_BOOKLET_CONFIRM_INFO`)}
        </ActionStatusModal>
      )}
      {synopsisToDelete && (
        <ActionStatusModal
          isOpened
          onClose={() => setSynopsisToDelete(null)}
          type="warning"
          title={t(`${TP}.FN_DELETE_SYNOPSIS_CONFIRM_TITLE`)}
          buttons={[
            <TextButton onClick={() => setSynopsisToDelete(null)} key="cancel-button">
              {t(`${TP}.m_CANCEL`)}
            </TextButton>,
            <Button onClick={deleteSynopsis} key="delete-button">
              {t(`${TP}.m_DELETE`)}
            </Button>,
          ]}
        >
          {t(`${TP}.FN_DELETE_SYNOPSIS_CONFIRM_INFO`)}
        </ActionStatusModal>
      )}
      {synopsisToEdit && (
        <AddSynopsisModal
          loadingFieldName={loadingFieldName}
          onSubmit={uploadSynopsis}
          langs={langs}
          document={synopsisToEdit}
          onClose={() => setSynopsisToEdit(null)}
        />
      )}
    </>
  );
};

SynopsisSection.propTypes = {
  production: PropTypes.object.isRequired,
  langs: PropTypes.array,
  synopsisData: PropTypes.shape({
    loadedOnce: PropTypes.bool,
    loading: PropTypes.bool,
    documents: PropTypes.arrayOf(
      PropTypes.shape({
        synopsis: PropTypes.object,
        acts: PropTypes.array,
      }),
    ),
  }),
  bufferData: PropTypes.shape({
    loading: PropTypes.bool,
  }),
  loadingFieldName: PropTypes.string,
  booklets: PropTypes.array,
};

SynopsisSection.defaultProps = {};

const mapStateToProps = createStructuredSelector({
  langs: selectLanguagesOptions(),
  synopsisData: selectProductionSynopsis(),
  bufferData: selectBufferValueData(),
  loadingFieldName: selectLoadingFieldName(),
  booklets: selectProductionBooklets(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(memo, withConnect)(SynopsisSection);
